import { useMemo, useState } from 'react';
import { Button, Checkbox, Input, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faPenToSquare,
  faRotateRight,
  faUsersGear,
} from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { enabledMechanisms, type StaffSalaryListItem } from '@salaries/shared';
import { useAuth } from '../../auth/AuthContext';
import { listStaffSalarySettings } from '../../api/salarySettings';
import { refreshStaffMembers } from '../../api/staffMembers';
import { openArboxProfile } from '../../api/payroll';
import { YesNoTag } from '../../components/YesNoTag';
import { EmptyState } from '../../components/EmptyState';
import { Loader, loaderIndicator } from '../../components/Loader';
import { useTableScrollY } from '../../components/useTableScrollY';
import { StaffSalaryEditDrawer } from './StaffSalaryEditDrawer';
import { parseRoles } from '../payroll/roles';


export function StaffSalarySettingsPage() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const mechanisms = auth.settings ? enabledMechanisms(auth.settings) : ['group_classes'];
  const isHugim = mechanisms.includes('hugim');
  const isTimeClock = mechanisms.includes('time_clock');
  const isGroupClasses = mechanisms.includes('group_classes');
  // Spec: default filter hides staff with all-zero configuration. Toggleable so
  // a fresh box (nobody configured yet) can still reach everyone.
  const [hideUnconfigured, setHideUnconfigured] = useState(true);
  // Staff Arbox no longer lists as active. Hidden by default — they're only of
  // interest when one of them is still owed a period (then the operator turns
  // "בשכר" on and they appear in the report like anyone else). Anyone already
  // IN payroll is always shown, deactivated or not, so their line is never
  // invisible here.
  const [showInactive, setShowInactive] = useState(false);
  // Staff without the Arbox COACH role (office, marketing, reception…). Hidden
  // by default — they're only here to be given a שכר גלובלי. Anyone who
  // already HAS a global salary is always shown.
  const [showNonCoaches, setShowNonCoaches] = useState(false);
  const [edit, setEdit] = useState<{ id: number; name: string } | null>(null);

  // Header stays fixed while rows scroll. Reserve = the table's own header +
  // pagination chrome (~96px) and a gutter.
  const [tableWrapperRef, tableScrollY] = useTableScrollY(96 + 16);

  const listQuery = useQuery({
    queryKey: ['salary-settings'],
    queryFn: listStaffSalarySettings,
  });

  const refreshMutation = useMutation({
    mutationFn: refreshStaffMembers,
    onSuccess: () => {
      toast.success('רשימת אנשי הצוות עודכנה');
      void queryClient.invalidateQueries({ queryKey: ['salary-settings'] });
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה ברענון'),
  });

  // Only coaches (Arbox role COACH = מאמן/מדריך/מטפל) are paid here — EXCEPT on
  // a time_clock box, whose regular employees almost never carry that role
  // (they might be REGULAR, SERVICE, RECEPTION, or anything else), so no role
  // filter is applied there; `tracked` is still the operator's on/off switch.
  const isCoach = (r: StaffSalaryListItem) => parseRoles(r.user_role).includes('COACH');
  const rows = isTimeClock || showNonCoaches
    ? (listQuery.data ?? [])
    : (listQuery.data ?? []).filter((r) => isCoach(r) || r.global_salary_amount != null);
  const nonCoachesHidden = useMemo(
    () =>
      (listQuery.data ?? []).filter((r) => !isCoach(r) && r.global_salary_amount == null).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listQuery.data],
  );

  const filtered = useMemo(() => {
    return rows.filter(
      (r) =>
        // A non-coach shown on purpose is shown to be configured — never
        // hide them as "unconfigured", that is the state they start in.
        !(hideUnconfigured && !r.any_configured && !(showNonCoaches && !isCoach(r))) &&
        !(!showInactive && !r.active && !r.tracked),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hideUnconfigured, showInactive, showNonCoaches]);

  const inactiveHidden = useMemo(
    () => rows.filter((r) => !r.active && !r.tracked).length,
    [rows],
  );

  // Name cells link to the staff member's Arbox profile (opens in a new tab),
  // matching the report tab's clickable name.
  const nameCell = (v: string | null, r: StaffSalaryListItem) =>
    v ? (
      <button
        type="button"
        onClick={() => openArboxProfile(r.arbox_users_boxes_id)}
        className="text-primary hover:underline cursor-pointer text-start"
        title="פתח פרופיל בארבוקס"
      >
        {v}
      </button>
    ) : (
      '—'
    );

  // A text column with a per-column search box + alphabetical sort (Hebrew-aware).
  const textCol = (dataIndex: 'first_name' | 'last_name' | 'phone' | 'email') => ({
    sorter: (a: StaffSalaryListItem, b: StaffSalaryListItem) =>
      String(a[dataIndex] ?? '').localeCompare(String(b[dataIndex] ?? ''), 'he'),
    filterIcon: (filtered: boolean) => (
      <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: filtered ? '#1936f7' : undefined }} />
    ),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div className="p-2" style={{ width: 220 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          placeholder="חיפוש"
          value={(selectedKeys[0] as string) ?? ''}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          className="mb-2"
        />
        <div className="flex gap-2 justify-end">
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>איפוס</Button>
          <Button size="small" type="primary" onClick={() => confirm()}>חיפוש</Button>
        </div>
      </div>
    ),
    onFilter: (value: any, r: StaffSalaryListItem) =>
      String(r[dataIndex] ?? '').toLowerCase().includes(String(value).toLowerCase()),
  });

  // A Yes/No config column: checkbox filter (כן/לא) + sort.
  const yesNoCol = (key: 'base' | 'weekend' | 'travel') => ({
    align: 'center' as const,
    width: 110,
    sorter: (a: StaffSalaryListItem, b: StaffSalaryListItem) =>
      Number(a.configured[key]) - Number(b.configured[key]),
    filters: [
      { text: 'כן', value: true },
      { text: 'לא', value: false },
    ],
    onFilter: (value: any, r: StaffSalaryListItem) => r.configured[key] === value,
    render: (_: unknown, r: StaffSalaryListItem) => <YesNoTag value={r.configured[key]} />,
  });

  const columns: ColumnsType<StaffSalaryListItem> = [
    { title: 'שם פרטי', dataIndex: 'first_name', width: 130, render: nameCell, ...textCol('first_name') },
    { title: 'שם משפחה', dataIndex: 'last_name', width: 130, render: nameCell, ...textCol('last_name') },
    {
      title: 'טלפון',
      dataIndex: 'phone',
      width: 140,
      render: (v: string | null) => (v ? <span dir="ltr">{v}</span> : '—'),
      ...textCol('phone'),
    },
    {
      title: 'אימייל',
      dataIndex: 'email',
      width: 240,
      render: (v: string | null) => (v ? <span dir="ltr">{v}</span> : '—'),
      ...textCol('email'),
    },
    // Base/travel are generic — shown whenever the box runs group_classes or
    // time_clock (either or both; independent of whether hugim is ALSO on).
    // Weekend bonus is class-attendance-based — group_classes only.
    ...((isGroupClasses || isTimeClock
      ? [
          { title: 'שכר בסיס', key: 'base', ...yesNoCol('base') },
          ...(isGroupClasses ? [{ title: 'תוספת סופ"ש', key: 'weekend', ...yesNoCol('weekend') }] : []),
          { title: 'נסיעות', key: 'travel', ...yesNoCol('travel') },
        ]
      : []) as ColumnsType<StaffSalaryListItem>),
    {
      title: 'שכר גלובלי',
      key: 'global_salary',
      align: 'center' as const,
      width: 120,
      sorter: (a: StaffSalaryListItem, b: StaffSalaryListItem) =>
        (a.global_salary_amount ?? -1) - (b.global_salary_amount ?? -1),
      filters: [
        { text: 'כן', value: true },
        { text: 'לא', value: false },
      ],
      onFilter: (value: any, r: StaffSalaryListItem) => (r.global_salary_amount != null) === value,
      render: (_: unknown, r: StaffSalaryListItem) =>
        r.global_salary_amount != null ? (
          <span dir="ltr">₪{r.global_salary_amount.toLocaleString('he-IL')}</span>
        ) : (
          '—'
        ),
    },
    {
      title: 'פעיל בארבוקס',
      key: 'active',
      align: 'center' as const,
      width: 120,
      sorter: (a: StaffSalaryListItem, b: StaffSalaryListItem) =>
        Number(a.active) - Number(b.active),
      filters: [
        { text: 'כן', value: true },
        { text: 'לא', value: false },
      ],
      onFilter: (value: any, r: StaffSalaryListItem) => r.active === value,
      render: (_: unknown, r: StaffSalaryListItem) => <YesNoTag value={r.active} />,
    },
    {
      title: 'בשכר',
      key: 'tracked',
      align: 'center' as const,
      width: 100,
      sorter: (a: StaffSalaryListItem, b: StaffSalaryListItem) =>
        Number(a.tracked) - Number(b.tracked),
      filters: [
        { text: 'כן', value: true },
        { text: 'לא', value: false },
      ],
      onFilter: (value: any, r: StaffSalaryListItem) => r.tracked === value,
      render: (_: unknown, r: StaffSalaryListItem) => <YesNoTag value={r.tracked} />,
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 64,
      render: (_, r) => (
        <Tooltip title="עריכת פרטי שכר מתקדמים">
          <button
            type="button"
            aria-label="עריכת פרטי שכר מתקדמים"
            className="p-2 rounded text-primary hover:bg-blue-bg"
            onClick={() => setEdit({ id: r.arbox_users_boxes_id, name: r.display_name })}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
        </Tooltip>
      ),
    },
  ];

  // Numeric total width → table-layout:fixed, so the fixed header and the
  // scrolling body line up (see useTableScrollY).
  const scrollX = columns.reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : 100), 0);

  if (listQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader size={48} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-small text-main-text cursor-pointer">
          <Checkbox
            checked={hideUnconfigured}
            onChange={(e) => setHideUnconfigured(e.target.checked)}
          />
          הסתר אנשי צוות ללא הגדרות שכר
        </label>
        <label className="flex items-center gap-2 text-small text-main-text cursor-pointer">
          <Checkbox checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          הצג אנשי צוות שאינם פעילים בארבוקס
          {inactiveHidden > 0 && !showInactive ? ` (${inactiveHidden})` : ''}
        </label>
        {!isTimeClock && (
          <label className="flex items-center gap-2 text-small text-main-text cursor-pointer">
            <Checkbox checked={showNonCoaches} onChange={(e) => setShowNonCoaches(e.target.checked)} />
            הצג אנשי צוות שאינם מאמנים
            {nonCoachesHidden > 0 && !showNonCoaches ? ` (${nonCoachesHidden})` : ''}
          </label>
        )}
        <div className="flex-1" />
        <span className="text-small text-secondary-text">{filtered.length} אנשי צוות</span>
        <Button
          icon={<FontAwesomeIcon icon={faRotateRight} />}
          loading={refreshMutation.isPending}
          onClick={() => refreshMutation.mutate()}
        >
          רענון אנשי צוות
        </Button>
      </div>

      <div ref={tableWrapperRef} className="flex-1 min-h-0 flex flex-col">
        <Table<StaffSalaryListItem>
          rowKey="arbox_users_boxes_id"
          size="small"
          columns={columns}
          dataSource={filtered}
          loading={
            listQuery.isFetching
              ? { spinning: true, indicator: loaderIndicator('sm') }
              : false
          }
          scroll={{ x: scrollX, y: tableScrollY }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
          locale={{
            emptyText:
              rows.length > 0 && hideUnconfigured ? (
                <EmptyState
                  icon={<FontAwesomeIcon icon={faUsersGear} style={{ fontSize: 42 }} />}
                  title="אין אנשי צוות עם הגדרות שכר"
                  description="כל אנשי הצוות עדיין ללא הגדרות. הצג את כולם כדי להתחיל להגדיר."
                  cta={
                    <Button type="primary" onClick={() => setHideUnconfigured(false)}>
                      הצג את כל אנשי הצוות
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<FontAwesomeIcon icon={faUsersGear} style={{ fontSize: 42 }} />}
                  title="אין אנשי צוות"
                  description="לא נמצאו אנשי צוות פעילים בעסק. נסה לרענן את הרשימה."
                />
              ),
          }}
        />
      </div>

      <StaffSalaryEditDrawer
        open={edit !== null}
        staffId={edit?.id ?? null}
        staffName={edit?.name ?? ''}
        onClose={() => setEdit(null)}
        onSaved={() => void listQuery.refetch()}
      />
    </div>
  );
}
